import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Job } from '../../types/backend.types';
import { JobWithDistance } from '../../types/extended.types';
import { geofenceService } from '../../services/geofence.service';
import { useLocation } from '../../contexts/LocationContext';
import './JobSelector.css';

const SELECTION_COOLDOWN = 2000; // 2 second cooldown after manual selection

interface JobSelectorProps {
  jobs: Job[];
  currentJob: Job | null;
  onJobSelect: (job: Job) => void;
  mode: 'gps' | 'admin'; // gps = 0.5 mile filter, admin = all jobs with search
  language?: 'en' | 'es';
}

export const JobSelector: React.FC<JobSelectorProps> = ({
  jobs,
  currentJob,
  onJobSelect,
  mode,
  language = 'en'
}) => {
  const { location, isLoading, forceRefreshLocation } = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayJobs, setDisplayJobs] = useState<JobWithDistance[]>([]);
  const [userAtJobSite, setUserAtJobSite] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);

  // Track when a manual selection was made to prevent race conditions
  const lastManualSelectionRef = useRef<number>(0);

  const translations = {
    en: {
      currentJob: 'CURRENT JOB',
      selectJob: 'Select a job site',
      clickToSelect: 'Click to select job',
      notAtJobSite: 'You are not at any job site',
      nearbyJobs: 'Nearby Job Sites (within 0.5 miles)',
      allJobs: 'All Job Sites',
      noNearbyJobs: 'No job sites within 0.5 miles',
      noJobsFound: 'No jobs found',
      searchJobs: 'Search jobs...',
      cancel: 'Cancel',
      milesAway: 'miles away',
      selected: '(Selected)',
      selectJobFirst: 'Please select a job to continue',
      loadingLocation: 'Getting location...',
      searchingForLocation: 'Searching for your location...',
      locationFound: 'Location found!',
      locationNotFound: 'Could not find location'
    },
    es: {
      currentJob: 'TRABAJO ACTUAL',
      selectJob: 'Seleccionar un sitio de trabajo',
      clickToSelect: 'Haga clic para seleccionar trabajo',
      notAtJobSite: 'No estás en ningún sitio de trabajo',
      nearbyJobs: 'Sitios Cercanos (dentro de 0.5 millas)',
      allJobs: 'Todos los Sitios de Trabajo',
      noNearbyJobs: 'No hay sitios dentro de 0.5 millas',
      noJobsFound: 'No se encontraron trabajos',
      searchJobs: 'Buscar trabajos...',
      cancel: 'Cancelar',
      milesAway: 'millas de distancia',
      selected: '(Seleccionado)',
      selectJobFirst: 'Por favor seleccione un trabajo para continuar',
      loadingLocation: 'Obteniendo ubicación...',
      searchingForLocation: 'Buscando tu ubicación...',
      locationFound: '¡Ubicación encontrada!',
      locationNotFound: 'No se pudo encontrar la ubicación'
    }
  };

  const t = translations[language || 'en'];

  const isUserWithinJobGeofence = useCallback((job: Job, userLat: number, userLng: number): boolean => {
    if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
      return geofenceService.isInsideCircle(
        userLat,
        userLng,
        job.lat,
        job.lng,
        job.geofence_data.radius
      );
    } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
      return geofenceService.isInsidePolygon(
        userLat,
        userLng,
        job.geofence_data.points
      );
    }

    const distance = geofenceService.calculateDistance(userLat, userLng, job.lat, job.lng);
    return distance <= 100;
  }, []);

  /**
   * Calculate distances and filter jobs based on mode
   */
  const processJobs = useCallback(() => {
    if (!jobs.length) {
      setDisplayJobs([]);
      setUserAtJobSite(false);
      return;
    }

    // If no location, show all jobs sorted by job number (newest first)
    if (!location) {
      const jobsWithoutDistance = jobs.map(job => ({
        ...job,
        distance: undefined,
        distanceValue: undefined,
        distanceInMiles: undefined,
        isWithinGeofence: false
      })).sort((a, b) => {
        // Sort by job number descending (newest first)
        // Job numbers like "25-100", "25-101" - extract the number after the dash
        const aNum = parseInt(a.bradanna_job_number?.split('-')[1] || '0', 10);
        const bNum = parseInt(b.bradanna_job_number?.split('-')[1] || '0', 10);
        return bNum - aNum;
      });
      setDisplayJobs(jobsWithoutDistance);
      setUserAtJobSite(false);
      return;
    }

    // Calculate distances for all jobs
    const jobsWithDist: JobWithDistance[] = jobs.map(job => {
      const distanceInMeters = geofenceService.calculateDistance(
        location.latitude,
        location.longitude,
        job.lat,
        job.lng
      );

      const distanceInMiles = geofenceService.metersToMiles(distanceInMeters);
      const isWithinGeofence = isUserWithinJobGeofence(job, location.latitude, location.longitude);

      return {
        ...job,
        distance: `${distanceInMiles.toFixed(2)} ${t.milesAway}`,
        distanceValue: distanceInMeters,
        distanceInMiles: distanceInMiles,
        isWithinGeofence: isWithinGeofence
      };
    }).sort((a, b) => (a.distanceValue || 0) - (b.distanceValue || 0));

    const jobUserIsAt = jobsWithDist.find(job => job.isWithinGeofence);
    setUserAtJobSite(!!jobUserIsAt);

    // Check if we're in cooldown period after manual selection
    const timeSinceLastSelection = Date.now() - lastManualSelectionRef.current;
    const inCooldown = timeSinceLastSelection < SELECTION_COOLDOWN;

    if (inCooldown) {
      console.log('[JobSelector] In cooldown period, skipping auto-select');
      // Still update display jobs but don't auto-select
      if (mode === 'gps') {
        setDisplayJobs(jobsWithDist.filter(job => (job.distanceInMiles || 0) <= 0.5));
      } else {
        setDisplayJobs(jobsWithDist);
      }
      return;
    }

    // For GPS mode: filter to 0.5 mile radius
    // For admin mode: show all jobs
    if (mode === 'gps') {
      const nearby = jobsWithDist.filter(job => (job.distanceInMiles || 0) <= 0.5);
      setDisplayJobs(nearby);

      // Auto-select job if user is at a job site
      if (!currentJob && jobUserIsAt) {
        onJobSelect(jobUserIsAt);
        localStorage.setItem('current_job_id', jobUserIsAt.job_id.toString());
      }
    } else {
      // Admin mode: show all jobs (no auto-select, admin picks manually per module)
      setDisplayJobs(jobsWithDist);

      // Only auto-select if at a job site and no job selected yet
      if (!currentJob && jobUserIsAt) {
        onJobSelect(jobUserIsAt);
        localStorage.setItem('current_job_id', jobUserIsAt.job_id.toString());
      }
    }
  }, [location, jobs, currentJob, onJobSelect, t.milesAway, isUserWithinJobGeofence, mode]);

  useEffect(() => {
    processJobs();
  }, [processJobs]);

  /**
   * Handle job selector click - refresh location for both modes
   * Admin mode also gets location to auto-detect nearby jobs
   */
  const handleSelectorClick = async () => {
    setIsModalOpen(true);

    // Search for location in both modes
    // Admin mode: helps auto-detect if at a job site
    // GPS mode: required for job selection
    setIsSearchingLocation(true);
    try {
      const result = await forceRefreshLocation();
      console.log(mode === 'admin' ? '📍 Admin location search result:' : '📍 GPS location result:', result);
    } finally {
      setIsSearchingLocation(false);
    }
  };

  /**
   * Filter and sort jobs by search term (for admin mode)
   * Priority: 1) Name match, 2) Job number match, 3) Address/City match
   */
  const filteredJobs = React.useMemo(() => {
    if (!searchTerm || mode !== 'admin') {
      return displayJobs;
    }

    const search = searchTerm.toLowerCase();

    // Filter jobs that match any field
    const matches = displayJobs.filter(job =>
      job.job_name.toLowerCase().includes(search) ||
      job.bradanna_job_number?.toLowerCase().includes(search) ||
      job.address?.toLowerCase().includes(search) ||
      job.city?.toLowerCase().includes(search)
    );

    // Sort by match priority: name starts with > name contains > job number > other
    return matches.sort((a, b) => {
      // Priority 1: Job name STARTS with search term
      const aStartsWithName = a.job_name.toLowerCase().startsWith(search);
      const bStartsWithName = b.job_name.toLowerCase().startsWith(search);
      if (aStartsWithName && !bStartsWithName) return -1;
      if (!aStartsWithName && bStartsWithName) return 1;

      // Priority 2: Job name CONTAINS search term
      const aContainsName = a.job_name.toLowerCase().includes(search);
      const bContainsName = b.job_name.toLowerCase().includes(search);
      if (aContainsName && !bContainsName) return -1;
      if (!aContainsName && bContainsName) return 1;

      // Priority 3: Job number matches
      const aNumMatch = a.bradanna_job_number?.toLowerCase().includes(search);
      const bNumMatch = b.bradanna_job_number?.toLowerCase().includes(search);
      if (aNumMatch && !bNumMatch) return -1;
      if (!aNumMatch && bNumMatch) return 1;

      // Then sort by distance if available, otherwise by job number (newest first)
      if (a.distanceValue && b.distanceValue) {
        return (a.distanceValue || 0) - (b.distanceValue || 0);
      }
      // No distance - sort by job number descending (newest first)
      const aNum = parseInt(a.bradanna_job_number?.split('-')[1] || '0', 10);
      const bNum = parseInt(b.bradanna_job_number?.split('-')[1] || '0', 10);
      return bNum - aNum;
    });
  }, [searchTerm, mode, displayJobs]);

  // Get properly typed current job with distance
  const currentJobTyped = currentJob
    ? displayJobs.find(j => j.job_id === currentJob.job_id) || currentJob
    : null;

  return (
    <>
      <div className="job-selector" onClick={handleSelectorClick}>
        <div className="job-selector-content">
          <div className="job-selector-label">{t.currentJob}</div>
          {currentJobTyped ? (
            <>
              <div className="job-name">{currentJobTyped.job_name}</div>
              <div className="job-address">
                <span className="location-icon">📍</span>
                <span>{currentJobTyped.address}, {currentJobTyped.city}</span>
                {('distance' in currentJobTyped) && currentJobTyped.distance && (
                  <span className="job-distance">• {String(currentJobTyped.distance)}</span>
                )}
              </div>
            </>
          ) : (
            <div className="job-placeholder">
              {isLoading ? (
                <span>{t.loadingLocation}</span>
              ) : mode === 'gps' && !userAtJobSite && location ? (
                <div className="not-at-job-warning">
                  <span>⚠️ {t.notAtJobSite}</span>
                  <small>{t.selectJobFirst}</small>
                </div>
              ) : (
                <span>{t.clickToSelect}</span>
              )}
            </div>
          )}
        </div>
        <div className="job-selector-arrow">▼</div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{mode === 'admin' ? t.allJobs : t.nearbyJobs}</h2>

            {/* Location search notification for admin mode */}
            {mode === 'admin' && isSearchingLocation && (
              <div style={{
                backgroundColor: '#dbeafe',
                color: '#1e40af',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                <span style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>📍</span>
                {t.searchingForLocation}
              </div>
            )}

            {/* Show "at job site" notification when location found */}
            {mode === 'admin' && !isSearchingLocation && userAtJobSite && location && (
              <div style={{
                backgroundColor: '#dcfce7',
                color: '#166534',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                <span>✅</span>
                {t.locationFound} - You're at a job site!
              </div>
            )}

            {/* Admin mode: search bar */}
            {mode === 'admin' && (
              <input
                type="text"
                placeholder={t.searchJobs}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontSize: '15px'
                }}
                onClick={e => e.stopPropagation()}
              />
            )}

            {!location && (
              <div className="location-warning">
                ⚠️ {isLoading ? t.loadingLocation : 'Location not available'}
              </div>
            )}

            {mode === 'gps' && !userAtJobSite && location && (
              <div className="not-at-job-banner">
                ⚠️ {t.notAtJobSite}
              </div>
            )}

            {currentJobTyped && (
              <div className="current-job-display">
                <div
                  className="job-item selected"
                  onClick={() => {
                    setIsModalOpen(false);
                    setSearchTerm('');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="job-item-header">
                    <div className="job-item-name">
                      {currentJobTyped.job_name} {t.selected}
                    </div>
                    {('distance' in currentJobTyped) && currentJobTyped.distance && (
                      <div className="job-item-distance">{String(currentJobTyped.distance)}</div>
                    )}
                  </div>
                  <div className="job-item-details">
                    {currentJobTyped.bradanna_job_number}
                  </div>
                  <div className="job-item-address">
                    📍 {currentJobTyped.address}, {currentJobTyped.city}, {currentJobTyped.state}
                  </div>
                </div>
              </div>
            )}

            {currentJobTyped && <hr style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />}

            <div className="jobs-list">
              {filteredJobs.length > 0 ? (
                filteredJobs
                  .filter(job => !currentJob || job.job_id !== currentJob.job_id)
                  .map(job => (
                    <div
                      key={job.job_id}
                      className={`job-item ${job.isWithinGeofence ? 'within-geofence' : ''}`}
                      onClick={() => {
                        // Set cooldown to prevent race conditions
                        lastManualSelectionRef.current = Date.now();
                        onJobSelect(job);
                        setIsModalOpen(false);
                        setSearchTerm('');
                        localStorage.setItem('current_job_id', job.job_id.toString());
                      }}
                    >
                      <div className="job-item-header">
                        <div className="job-item-name">
                          {job.job_name}
                          {job.isWithinGeofence && <span className="at-location-badge">📍 At Location</span>}
                        </div>
                        {job.distance && (
                          <div className="job-item-distance">{job.distance}</div>
                        )}
                      </div>
                      <div className="job-item-details">
                        {job.bradanna_job_number}
                      </div>
                      <div className="job-item-address">
                        📍 {job.address}, {job.city}, {job.state}
                      </div>
                    </div>
                  ))
              ) : (
                <div className="no-nearby-jobs">
                  {mode === 'admin' && searchTerm ? t.noJobsFound : t.noNearbyJobs}
                </div>
              )}
            </div>

            <button className="modal-close" onClick={() => {
              setIsModalOpen(false);
              setSearchTerm('');
            }}>
              {t.cancel}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
